"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useTransition, type ComponentProps, type ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { PortableText, stegaClean } from "next-sanity";
import { Modal } from "@repo/ui/mantine";
import { Typography } from "@repo/ui/aops-components/typography";
import { getPortableTextComponents, type PortableTextLinkMarkValue } from "@repo/ui/aops-components/portable-text-components";
import { DiscoveryButton } from "@/app/components/DiscoveryButton";
import { acceptPrivacyPolicyAction, persistPrivacyPolicyNoticeAction } from "@/app/actions/acceptPrivacyPolicy";
import { isPrivacyPolicyPath } from "@/lib/privacy/isPrivacyPolicyPath";
import { isAcceptPrivacyPolicyButton, type PrivacyPolicyAnnouncement } from "@/lib/privacy/privacyPolicyAnnouncement";
import type { PrivacyPolicyNoticeValue } from "@/lib/privacy/privacyPolicyNoticeCookie";
import { AGREED_NEW_PRIVACY_POLICY_EVENT, trackOpenedNewPrivacyPolicy, trackPrivacyPolicyEvent, trackPrivacyPolicyPopupViewed } from "@/lib/privacy/privacyPolicyTracking";
import portableTextStyles from "../PortableText/PortableTextComponents.module.css";
import styles from "./PrivacyPolicyChange2026Modal.module.css";
import { TrackPrivacyPolicyPageExit } from "./TrackPrivacyPolicyPageExit";
export type PrivacyPolicyChange2026ModalProps = {
  show: boolean;
  needsPersist: boolean;
  persistValue?: PrivacyPolicyNoticeValue;
  announcement: PrivacyPolicyAnnouncement | null;
};
const announcementPortableTextComponents = (() => {
  const components = getPortableTextComponents({
    classNames: {
      paragraph: portableTextStyles.paragraph,
      heading: portableTextStyles.heading,
      list: portableTextStyles.list,
      listItem: portableTextStyles.listItem,
      listNumber: portableTextStyles.listNumber,
      listItemNumber: portableTextStyles.listItemNumber
    }
  });
  return {
    ...components,
    marks: {
      ...components.marks,
      link: AnnouncementLinkMark
    }
  };
})() as ComponentProps<typeof PortableText>["components"];
function AnnouncementLinkMark(t0) {
  const $ = _c(22);
  const {
    children,
    value
  } = t0;
  let href;
  let newTabProps;
  let onClick;
  let t1;
  let t2;
  if ($[0] !== children || $[1] !== value?.href || $[2] !== value?.openInNewTab) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      href = stegaClean(value?.href);
      if (!href) {
        let t3;
        if ($[8] !== children) {
          t3 = <>{children}</>;
          $[8] = children;
          $[9] = t3;
        } else {
          t3 = $[9];
        }
        t2 = t3;
        break bb0;
      }
      const openInNewTab = value?.openInNewTab === true;
      let t3;
      if ($[10] !== openInNewTab) {
        t3 = openInNewTab ? {
          target: "_blank",
          rel: "noopener noreferrer"
        } : {};
        $[10] = openInNewTab;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      newTabProps = t3;
      onClick = () => {
        trackOpenedNewPrivacyPolicy(href);
      };
      t1 = href.startsWith("/");
    }
    $[0] = children;
    $[1] = value?.href;
    $[2] = value?.openInNewTab;
    $[3] = href;
    $[4] = newTabProps;
    $[5] = onClick;
    $[6] = t1;
    $[7] = t2;
  } else {
    href = $[3];
    newTabProps = $[4];
    onClick = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  if (t1) {
    let t3;
    if ($[12] !== children || $[13] !== href || $[14] !== newTabProps || $[15] !== onClick) {
      t3 = <Link href={href} onClick={onClick} {...newTabProps}>{children}</Link>;
      $[12] = children;
      $[13] = href;
      $[14] = newTabProps;
      $[15] = onClick;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  let t3;
  if ($[17] !== children || $[18] !== href || $[19] !== newTabProps || $[20] !== onClick) {
    t3 = <a href={href} onClick={onClick} {...newTabProps}>{children}</a>;
    $[17] = children;
    $[18] = href;
    $[19] = newTabProps;
    $[20] = onClick;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  return t3;
}
export function PrivacyPolicyChange2026Modal(t0) {
  const $ = _c(26);
  const {
    show,
    needsPersist,
    persistValue,
    announcement
  } = t0;
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(show);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isPending, startTransition] = useTransition();
  let t1;
  if ($[0] !== announcement || $[1] !== isOpen || $[2] !== pathname) {
    t1 = isOpen && !isPrivacyPolicyPath(pathname) && announcement !== null;
    $[0] = announcement;
    $[1] = isOpen;
    $[2] = pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const popupVisible = t1;
  let t2;
  let t3;
  if ($[4] !== needsPersist || $[5] !== persistValue) {
    t2 = () => {
      if (needsPersist && persistValue) {
        persistPrivacyPolicyNoticeAction(persistValue);
      }
    };
    t3 = [needsPersist, persistValue];
    $[4] = needsPersist;
    $[5] = persistValue;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== popupVisible) {
    t4 = () => {
      if (!popupVisible) {
        trackPrivacyPolicyPopupViewed(false, "");
        return;
      }
      trackPrivacyPolicyPopupViewed(true, window.location.href);
    };
    $[8] = popupVisible;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== pathname || $[11] !== popupVisible) {
    t5 = [popupVisible, pathname];
    $[10] = pathname;
    $[11] = popupVisible;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function handleAgree() {
      trackPrivacyPolicyEvent(AGREED_NEW_PRIVACY_POLICY_EVENT);
      setErrorMessage(null);
      startTransition(async () => {
        const result = await acceptPrivacyPolicyAction();
        if (!result.success) {
          setErrorMessage(result.errorMessage);
          return;
        }
        setIsOpen(false);
      });
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleAgree = t6;
  let t7;
  if ($[14] !== announcement || $[15] !== show) {
    t7 = show && announcement && <TrackPrivacyPolicyPageExit />;
    $[14] = announcement;
    $[15] = show;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== announcement || $[18] !== errorMessage || $[19] !== isOpen || $[20] !== isPending || $[21] !== popupVisible) {
    t8 = popupVisible && announcement && <Modal opened={isOpen} onClose={_temp} centered={true} withCloseButton={false} closeOnClickOutside={false} closeOnEscape={false} title={<Typography variant="h4" as="span" className={styles.title}>{announcement.title}</Typography>} classNames={{
      content: styles.content,
      body: styles.body
    }}><div className={portableTextStyles.portableText}><PortableText value={announcement.body} components={announcementPortableTextComponents} /></div>{errorMessage && <Typography variant="bodySmall" className={styles.errorMessage} role="alert">{errorMessage}</Typography>}{announcement.buttons.length > 0 && <div className={styles.actions}>{announcement.buttons.map(button => {
          const accept = isAcceptPrivacyPolicyButton(button.link);
          return <DiscoveryButton key={button._key} button={accept && isPending ? {
            ...button,
            disabled: true
          } : button} onClick={accept ? handleAgree : undefined} onLinkClick={accept ? undefined : trackOpenedNewPrivacyPolicy} />;
        })}</div>}</Modal>;
    $[17] = announcement;
    $[18] = errorMessage;
    $[19] = isOpen;
    $[20] = isPending;
    $[21] = popupVisible;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp() {}